import emailjs from '@emailjs/browser';
import { generateInvitationUrl } from '../utils/domainUtils';

// EmailJS configuration - these should be environment variables in production
const EMAIL_CONFIG = {
  SERVICE_ID: 'gmail_service', // Your Gmail service ID
  TEMPLATE_ID: 'wishlist_invite_collab', // Your template ID from EmailJS
  PUBLIC_KEY: 'YR4JN3u0pRnfVIaUh' // Your public key from EmailJS
};

/**
 * Initialize EmailJS
 */
export const initEmailService = () => {
  // In production, these values should come from environment variables
  if (false) { // Temporarily disabled to test real emails
    console.log('Email service initialized in development mode');
    return true;
  }
  
  try {
    emailjs.init(EMAIL_CONFIG.PUBLIC_KEY);
    return true;
  } catch (error) {
    console.error('Failed to initialize email service:', error);
    return false;
  }
};

/**
 * Generate a secure invitation token
 */
const generateInvitationToken = () => {
  // Generate a random 32-character token
  const chars = 'ABCDEFGHIJKLMNOPQRSTUVWXYZabcdefghijklmnopqrstuvwxyz0123456789';
  let token = '';
  for (let i = 0; i < 32; i++) {
    token += chars.charAt(Math.floor(Math.random() * chars.length));
  }
  return token;
};

/**
 * Send collaboration invitation email
 */
export const sendCollaborationInvite = async (invitationData) => {
  try {
    console.log('Preparing to send invitation email...', invitationData);

    // Use the pre-generated token from invitation data
    const invitationToken = invitationData.invitationToken || generateInvitationToken();

    // Email template parameters
    const templateParams = {
      to_email: invitationData.inviteeEmail,
      to_name: invitationData.inviteeEmail.split('@')[0], // Use email prefix as name
      from_name: invitationData.inviterName,
      wishlist_title: invitationData.wishlistTitle,
      wishlist_url: generateInvitationUrl(invitationToken),
      expiry_date: new Date(invitationData.expiresAt).toLocaleDateString(),
      message: `${invitationData.inviterName} has invited you to collaborate on their wishlist "${invitationData.wishlistTitle}". Click the link below to accept the invitation and start adding items together!`
    };

    // For development, just log the email content
    if (false) { // Temporarily disabled to test real emails
      console.log('📧 Email would be sent with template params:', templateParams);
      
      // Simulate email sending with a delay
      await new Promise(resolve => setTimeout(resolve, 1000));
      
      return {
        status: 200,
        text: 'Email simulated successfully (development mode)',
        invitationToken
      };
    }

    // In production, actually send the email
    const response = await emailjs.send(
      EMAIL_CONFIG.SERVICE_ID,
      EMAIL_CONFIG.TEMPLATE_ID,
      templateParams,
      EMAIL_CONFIG.PUBLIC_KEY
    );

    console.log('✅ Email sent successfully:', response);
    return {
      ...response,
      invitationToken
    };

  } catch (error) {
    console.error('❌ Failed to send invitation email:', error);
    throw new Error('Failed to send invitation email. Please try again.');
  }
};

/**
 * Email template for collaboration invitations
 * This is the template structure that should be configured in EmailJS
 */
export const EMAIL_TEMPLATE_STRUCTURE = {
  subject: 'You\'re invited to collaborate on {{wishlist_title}}!',
  html: `
    <div style="font-family: Arial, sans-serif; max-width: 600px; margin: 0 auto; padding: 20px;">
      <div style="background: linear-gradient(135deg, #8b5cf6 0%, #ec4899 100%); padding: 30px; border-radius: 15px; text-align: center; margin-bottom: 30px;">
        <h1 style="color: white; margin: 0; font-size: 28px; font-weight: bold;">
          🎁 Wishlist Collaboration Invite
        </h1>
      </div>
      
      <div style="background: #f8fafc; padding: 30px; border-radius: 15px; margin-bottom: 30px;">
        <h2 style="color: #1f2937; margin: 0 0 20px 0;">Hi {{to_name}}!</h2>
        
        <p style="color: #4b5563; font-size: 16px; line-height: 1.6; margin-bottom: 20px;">
          {{from_name}} has invited you to collaborate on their wishlist:
        </p>
        
        <div style="background: white; padding: 20px; border-radius: 10px; border-left: 4px solid #8b5cf6; margin: 20px 0;">
          <h3 style="color: #8b5cf6; margin: 0 0 10px 0;">{{wishlist_title}}</h3>
          <p style="color: #6b7280; margin: 0;">{{message}}</p>
        </div>
        
        <div style="text-align: center; margin: 30px 0;">
          <a href="{{wishlist_url}}" 
             style="background: linear-gradient(135deg, #8b5cf6 0%, #ec4899 100%); 
                    color: white; 
                    padding: 15px 30px; 
                    text-decoration: none; 
                    border-radius: 10px; 
                    font-weight: bold; 
                    display: inline-block;">
            Accept Invitation
          </a>
        </div>
        
        <p style="color: #9ca3af; font-size: 14px; text-align: center; margin-top: 30px;">
          This invitation expires on {{expiry_date}}
        </p>
      </div>
      
      <div style="text-align: center; color: #9ca3af; font-size: 12px;">
        <p>Sent by Wispin Wishlist App</p>
        <p>If you didn't expect this invitation, you can safely ignore this email.</p>
      </div>
    </div>
  `,
  text: `
Hi {{to_name}}!

{{from_name}} has invited you to collaborate on their wishlist "{{wishlist_title}}".

{{message}}

Accept the invitation: {{wishlist_url}}

This invitation expires on {{expiry_date}}.

---
Sent by Wispin Wishlist App
If you didn't expect this invitation, you can safely ignore this email.
  `
};

const emailService = {
  initEmailService,
  sendCollaborationInvite,
  EMAIL_TEMPLATE_STRUCTURE
};
export default emailService;